import React, { useState } from "react";
import { useAuth, errText } from "../AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { LogIn, Shield, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
    const { user, login } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({ username: "", password: "" });
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [showPw, setShowPw] = useState(false);

    if (user) return <Navigate to={user.role === "super_admin" ? "/super-admin" : "/admin"} replace />;

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        try {
            const data = await login(form.username.trim(), form.password);
            nav(data.role === "super_admin" ? "/super-admin" : "/admin", { replace: true });
        } catch (e2) {
            setErr(errText(e2, "Login fallito"));
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6 pt-24 pb-16 bg-gradient-to-br from-stone-50 via-emerald-50/40 to-amber-50/40" data-testid="login-page">
            <div className="w-full max-w-md bg-white border border-stone-200 rounded-3xl shadow-xl overflow-hidden">
                <div className="bg-gradient-to-br from-emerald-700 to-emerald-600 text-white p-8">
                    <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center mb-3">
                        <Shield size={22} />
                    </div>
                    <h1 className="font-serif text-3xl font-semibold">Area Riservata</h1>
                    <p className="text-emerald-100 text-sm mt-1">Accesso amministratori piattaforma VisitBorghi</p>
                </div>
                <form onSubmit={submit} className="p-8 space-y-5" data-testid="login-form">
                    <div>
                        <label className="text-sm font-medium text-stone-700 mb-2 block">Username</label>
                        <input
                            type="text"
                            required
                            className="input-elegant"
                            value={form.username}
                            onChange={(e) => setForm({ ...form, username: e.target.value })}
                            data-testid="login-username"
                            autoComplete="username"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-stone-700 mb-2 block">Password</label>
                        <div className="relative">
                            <input
                                type={showPw ? "text" : "password"}
                                required
                                className="input-elegant pr-11"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                data-testid="login-password"
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-emerald-700 p-1 rounded-md"
                                data-testid="login-password-toggle"
                                aria-label={showPw ? "Nascondi password" : "Mostra password"}
                                title={showPw ? "Nascondi password" : "Mostra password"}
                            >
                                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>
                    {err && (
                        <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
                            {err}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={busy}
                        className="btn-primary w-full py-3 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                        data-testid="login-submit"
                    >
                        <LogIn size={18} />
                        {busy ? "Accesso..." : "Accedi"}
                    </button>
                </form>
            </div>
        </div>
    );
}
